import React from 'react';
import { 
  Home, UserPlus, UsersRound, 
  Accessibility, PersonStanding,
  VenusAndMars
} from 'lucide-react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer
} from 'recharts';


// CUSTOM TOOLTIP
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const female = payload.find(p => p.dataKey === "female")?.value || 0;
    const male = payload.find(p => p.dataKey === "male")?.value || 0;
    const total = female + male;

    return (
      <div className="bg-white rounded-2xl shadow-xl px-4 py-3 border border-gray-100 min-w-[170px]">
        <p className="font-bold text-gray-800 mb-2">{label}</p>

        {/* Male */}
        <div className="flex items-center justify-between text-sm mb-1">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-[#D1E9FF]" />
            <span className="text-gray-600">Male</span>
          </div>
          <span className="font-semibold text-gray-800">{male}</span>
        </div>

        {/* Female */}
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-[#E9B7E9]" />
            <span className="text-gray-600">Female</span>
          </div>
          <span className="font-semibold text-gray-800">{female}</span>
        </div>

        {/* Total */}
        <div className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded-md font-medium">
          <span className="text-gray-600">Total</span>
          <span className="text-gray-800">{total}</span>
        </div>
      </div>
    );
  }
  return null;
};


// STATS CARD
const StatCard = ({ title, subtitle, value, icon: Icon, iconBg, iconColor, valueColor }) => (
  <div className="bg-gray-50 p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full">
    <div className="flex items-start mb-4">
      <div className={`p-3 rounded-xl ${iconBg} ${iconColor}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="text-left flex-1 pl-4">
        <h3 className="text-[1.125rem] font-bold text-gray-800 leading-tight">{title}</h3> 
        <p className="text-[0.90rem] text-gray-500 font-medium">{subtitle}</p>
      </div>
    </div>
    <div className="mt-auto text-right">
      <span className={`text-2xl font-extrabold ${valueColor}`}>{value}</span>
    </div>
  </div>
);


// MAIN COMPONENT
const ResidentsCard = () => {

  const chartData = [
    { name: 'Poblacion', female: 250, male: 150 },
    { name: 'Hangga 1', female: 310, male: 490 },
    { name: 'Hangga 2', female: 400, male: 200 },
    { name: 'Tambubong', female: 190, male: 500 },
    { name: 'Tabing-Ilog A', female: 400, male: 120 },
    { name: 'Tabing-Ilog B', female: 240, male: 270 },
    { name: 'Carmen De Luna', female: 390, male: 320 },
  ];

  const stats = [
    { title: "Total Population", subtitle: "Registered residents in the barangay", value: "1,195.35", icon: UserGroupIcon, iconBg: "bg-purple-100/60", iconColor: "text-purple-500", valueColor: "text-[#8b5cf6]" },
    { title: "Total Household", subtitle: "Number of recorded households", value: "244", icon: Home, iconBg: "bg-emerald-100/60", iconColor: "text-emerald-600", valueColor: "text-[#10b981]" },
    { title: "Total Family", subtitle: "Registered family units", value: "184", icon: UserPlus, iconBg: "bg-yellow-100/60", iconColor: "text-yellow-600", valueColor: "text-[#f59e0b]" },
    { title: "Total Senior Citizen", subtitle: "Residents aged 60 and above", value: "467", icon: UsersRound, iconBg: "bg-red-100/60", iconColor: "text-red-500", valueColor: "text-[#b91c1c]" },
    { title: "Total PWDs", subtitle: "Persons with disabilities recorded", value: "89", icon: Accessibility, iconBg: "bg-blue-100/60", iconColor: "text-blue-600", valueColor: "text-[#3b82f6]" },
    { title: "Total Solo Parents", subtitle: "Registered solo parent beneficiaries", value: "21", icon: PersonStanding, iconBg: "bg-orange-100/60", iconColor: "text-orange-500", valueColor: "text-[#f97316]" },
  ];

  return (
    <div className="bg-white p-5 rounded-2xl shadow-md">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Residents</h2>
        <p className="text-gray-600 font-semibold">Demographic Distribution & Sectoral Profiles</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        {stats.map((stat, index) => <StatCard key={index} {...stat} />)}
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-8">
          <VenusAndMars className="w-8 h-8 text-green-600" />
          <h3 className="font-bold text-gray-700">Total Male and Female (Per Purok)</h3>
        </div>

        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical stroke="#e5e7eb" horizontal={false} />

              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} domain={[0, 1000]} ticks={[0,100,200,300,400,500,600,700,800,900,1000]} />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />

              <Bar dataKey="female" stackId="a" fill="#E9B7E9" barSize={50} />
              <Bar dataKey="male" stackId="a" fill="#D1E9FF" barSize={50} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ResidentsCard;
